import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { WVE_LOGO_B64 } from "@/wveLogo";
import {
  Loader2, RefreshCw, Check, FileText, Send, Phone, Printer, Upload, X, ImageIcon, AlertTriangle,
  CheckCircle2, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { EditableField } from "@/components/EditableField";
import { API, isSheetOverdue } from "@/utils/constants";

const PhoneActions = ({ phone, testid }) => {
  if (!phone) return <p className="text-sm font-medium">{'\u2014'}</p>;
  const digits = phone.replace(/[^\d+]/g, '');
  const intl = digits.startsWith('+') ? digits.slice(1) : digits.startsWith('0') ? `44${digits.slice(1)}` : digits;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <a href={`tel:${digits}`} className="text-sm font-medium text-[#1a73e8] underline decoration-dotted hover:text-[#174ea6]" data-testid={`${testid}-call`}>
        {phone}
      </a>
      <a
        href={`https://wa.me/${intl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#e7f8ef] text-[#128c4b] hover:bg-[#d2f2e0] px-2 py-0.5 rounded-full transition-colors"
        data-testid={`${testid}-whatsapp`}
      >
        <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M12 2a10 10 0 0 0-8.7 14.9L2 22l5.3-1.4A10 10 0 1 0 12 2zm5.3 14.3c-.2.6-1.2 1.2-1.7 1.2-.4.1-1 .1-1.6-.1-.4-.1-.9-.3-1.5-.6-2.6-1.1-4.3-3.8-4.4-4-.1-.2-1.1-1.4-1.1-2.7s.7-1.9.9-2.2c.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2.1c.1.2.1.4 0 .5l-.4.6-.5.5c-.1.1-.2.3-.1.5.1.2.6 1 1.3 1.7.9.8 1.7 1.1 2 1.2.2.1.4.1.5-.1l.7-.9c.2-.2.3-.2.6-.1l1.8.9c.3.1.4.2.5.3 0 .2 0 .6-.2 1.1z"/></svg>
        WhatsApp
      </a>
    </div>
  );
};

export const JobSheetDialog = ({ open, onClose, jobId, editMode, authPassword, onSheetSent }) => {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState({});
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const photoUrlRef = useRef(null);

  useEffect(() => {
    if (open && jobId) {
      setLoading(true);
      setEditing({});
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      setPhotoUrl(null);
      photoUrlRef.current = null;
      axios.get(`${API}/job-sheet/${jobId}`)
        .then(res => {
          setSheet(res.data);
          if (res.data.couple_photo) {
            axios.get(`${API}/photos/${res.data.couple_photo}`, { responseType: 'blob' })
              .then(photoRes => {
                const url = URL.createObjectURL(photoRes.data);
                photoUrlRef.current = url;
                setPhotoUrl(url);
              })
              .catch((err) => { console.error('Failed to load couple photo:', err); });
          }
        })
        .catch(() => toast.error("Failed to load job sheet"))
        .finally(() => setLoading(false));
    }
  }, [open, jobId]);

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    setSheet(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (Object.keys(editing).length === 0) return;
    try {
      const res = await axios.put(`${API}/job-sheet/${jobId}`, editing, {
        headers: { 'X-Auth-Password': authPassword }
      });
      setSheet(res.data);
      setEditing({});
      toast.success("Job sheet saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleSend = async () => {
    if (Object.keys(editing).length > 0) await handleSave();
    setSending(true);
    try {
      const res = await axios.post(`${API}/job-sheet/${jobId}/send`, {}, {
        headers: { 'X-Auth-Password': authPassword }
      });
      toast.success(res.data.message);
      setSheet(prev => ({ ...prev, is_complete: true, send_status: 'sent', send_error: null, sent_at: new Date().toISOString(), sent_to: res.data.sent_to }));
      onSheetSent?.();
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "Failed to send";
      toast.error(errorDetail);
      setSheet(prev => ({ ...prev, send_status: 'failed', send_error: errorDetail }));
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/job-sheet/${jobId}/refresh`, {}, {
        headers: { 'X-Auth-Password': authPassword }
      });
      if (res.data.success === false) {
        toast.error(res.data.message);
      } else {
        setSheet(res.data);
        toast.success("Refreshed from Typeform");
      }
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/job-sheet/${jobId}/photo`, formData, {
        headers: { 'X-Auth-Password': authPassword, 'Content-Type': 'multipart/form-data' }
      });
      const res = await axios.get(`${API}/job-sheet/${jobId}`);
      setSheet(res.data);
      if (res.data.couple_photo) {
        const photoRes = await axios.get(`${API}/photos/${res.data.couple_photo}`, { responseType: 'blob' });
        if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
        const url = URL.createObjectURL(photoRes.data);
        photoUrlRef.current = url;
        setPhotoUrl(url);
      }
      toast.success("Photo uploaded");
    } catch {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async () => {
    try {
      await axios.delete(`${API}/job-sheet/${jobId}/photo`, {
        headers: { 'X-Auth-Password': authPassword }
      });
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      photoUrlRef.current = null;
      setPhotoUrl(null);
      setSheet(prev => ({ ...prev, couple_photo: null }));
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to remove photo");
    }
  };

  const formatDate = (d) => {
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return d; }
  };

  const handlePrint = async () => {
    if (!sheet) return;
    const esc = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/\n/g, '<br>');
    const couple = `${esc(sheet.person1_name)} & ${esc(sheet.person2_name)}`;
    const date = formatDate(sheet.job_date);

    let photoDataUrl = '';
    if (photoUrl) {
      try {
        const resp = await fetch(photoUrl);
        const blob = await resp.blob();
        photoDataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (err) { console.error('Failed to load photo for print:', err); }
    }

    const timelineRows = (sheet.timeline || '').split('\n').filter(l => l.trim()).map(line => {
      const match = line.match(/^(\d{1,2}(?:[.:]\d{2})?\s*(?:am|pm))\s*[–\-]\s*(.+)$/i);
      if (match) return `<tr><td class="tl-time">${esc(match[1])}</td><td class="tl-event">${esc(match[2])}</td></tr>`;
      return `<tr><td class="tl-time"></td><td class="tl-event">${esc(line)}</td></tr>`;
    }).join('');

    const section = (title, content) => {
      if (!content) return '';
      return `<div class="section"><div class="section-title">${esc(title)}</div><div class="section-body">${esc(content)}</div></div>`;
    };

    const teamBadges = (sheet.team || []).map(n => `<span class="team-badge">${esc(n)}</span>`).join('');

    const htmlContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Prep Sheet</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
<style>
@page { size: A4; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Inter', -apple-system, sans-serif; color: #2d2d2d; font-size: 9pt; line-height: 1.35; background: #fff; }
.page { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 0; overflow: hidden; }

/* Header */
.header { background: #3d3d3d; color: #fff; padding: 14px 24px; position: relative; overflow: hidden; }
.header::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, #c9a96e, #e8d5a3, #c9a96e); }
.header-content { display: flex; align-items: center; justify-content: space-between; }
.header-left { display: flex; align-items: center; gap: 14px; flex: 1; }
.header-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.header-logo { height: 44px; width: auto; opacity: 0.9; }
.header-label { font-family: 'Playfair Display', Georgia, serif; font-size: 13pt; font-weight: 700; letter-spacing: 2px; color: rgba(255,255,255,0.85); text-transform: uppercase; }
.header-photo { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(255,255,255,0.3); flex-shrink: 0; }
.header-text { flex: 1; }
.couple-name { font-family: 'Playfair Display', Georgia, serif; font-size: 20pt; font-weight: 700; letter-spacing: 0.3px; line-height: 1.1; }
.header-date { color: rgba(255,255,255,0.7); font-size: 9pt; margin-top: 2px; }
.team-badges { margin-top: 5px; display: flex; gap: 4px; flex-wrap: wrap; }
.team-badge { background: rgba(255,255,255,0.15); padding: 1px 8px; border-radius: 2px; font-size: 7.5pt; font-weight: 500; }

/* Body */
.body { padding: 10px 24px 14px; }

/* Contact row */
.contacts { display: flex; gap: 10px; margin-bottom: 8px; }
.contact-card { flex: 1; background: #f8f7f5; border-radius: 4px; padding: 6px 10px; border-left: 3px solid #c9a96e; }
.contact-name { font-size: 7.5pt; color: #888; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 600; }
.contact-phone { font-size: 10pt; font-weight: 600; color: #2d2d2d; margin-top: 1px; }

/* Info grid */
.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e8e8e8; }
.info-item .info-label { font-size: 7pt; color: #999; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600; margin-bottom: 0; }
.info-item .info-value { font-size: 9pt; font-weight: 500; }

/* Video Booth badge */
.booth-badge { display: inline-block; background: #3d3d3d; color: #fff; padding: 3px 12px; border-radius: 3px; font-weight: 700; font-size: 8pt; letter-spacing: 1px; margin-bottom: 8px; margin-right: 6px; width: fit-content; }

/* Timeline table */
.timeline-header { font-family: 'Playfair Display', Georgia, serif; font-size: 11pt; font-weight: 700; color: #3d3d3d; margin-bottom: 3px; padding-bottom: 2px; border-bottom: 2px solid #c9a96e; display: inline-block; }
.timeline-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
.tl-time { width: 55px; padding: 2px 8px 2px 0; font-weight: 600; color: #c9a96e; font-size: 8.5pt; vertical-align: top; white-space: nowrap; border-bottom: 1px solid #f0f0f0; }
.tl-event { padding: 2px 0; font-size: 8.5pt; border-bottom: 1px solid #f0f0f0; }

/* Sections */
.section { margin-bottom: 6px; }
.section-title { font-family: 'Playfair Display', Georgia, serif; font-size: 10pt; font-weight: 700; color: #3d3d3d; margin-bottom: 1px; padding-bottom: 1px; border-bottom: 1px solid #e8e8e8; }
.section-body { font-size: 8.5pt; line-height: 1.4; white-space: pre-line; color: #444; }

/* Footer */
.footer { text-align: center; margin-top: 8px; padding-top: 6px; border-top: 1px solid #c9a96e; font-size: 7pt; color: #999; letter-spacing: 0.5px; text-transform: uppercase; }

@media print {
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 100%; min-height: auto; }
}
</style></head><body>
<div class="page">
  <div class="header">
    <div class="header-content">
      <div class="header-left">
        ${photoDataUrl ? `<img src="${photoDataUrl}" class="header-photo" alt="Couple">` : ''}
        <div class="header-text">
          <div class="couple-name">${couple}</div>
          <div class="header-date">${esc(date)}${sheet.entertainment_required ? ` — ${esc(sheet.entertainment_required)}` : ''}</div>
          ${teamBadges ? `<div class="team-badges">${teamBadges}</div>` : ''}
        </div>
      </div>
      <div class="header-right">
        <span class="header-label">${sheet.sheet_type === 'guestbook' ? 'Guestbook Sheet' : 'Prep Sheet'}</span>
        <img src="${WVE_LOGO_B64}" class="header-logo" alt="WVE" />
      </div>
    </div>
  </div>
  <div class="body">
    <div class="contacts">
      <div class="contact-card">
        <div class="contact-name">${esc(sheet.person1_name)}</div>
        <div class="contact-phone">${esc(sheet.person1_phone) || '\u2014'}</div>
      </div>
      <div class="contact-card">
        <div class="contact-name">${esc(sheet.person2_name)}</div>
        <div class="contact-phone">${esc(sheet.person2_phone) || '\u2014'}</div>
      </div>
    </div>
    <div class="info-grid">
      ${sheet.sheet_type === 'guestbook' ? `
      ` : `
        ${(!sheet.ceremony_venue && !sheet.reception_venue && sheet.venue) ? `<div class="info-item"><div class="info-label">Venue</div><div class="info-value">${esc(sheet.venue)}</div></div>` : ''}
        ${sheet.ceremony_venue ? `<div class="info-item"><div class="info-label">Ceremony</div><div class="info-value">${esc(sheet.ceremony_venue)}</div></div>` : ''}
        ${sheet.reception_venue ? `<div class="info-item"><div class="info-label">Reception</div><div class="info-value">${esc(sheet.reception_venue)}</div></div>` : ''}
        ${sheet.coordinator ? `<div class="info-item"><div class="info-label">Coordinator</div><div class="info-value">${esc(sheet.coordinator)}</div></div>` : ''}
        ${sheet.guest_count ? `<div class="info-item"><div class="info-label">Guests</div><div class="info-value">${esc(String(sheet.guest_count))}</div></div>` : ''}
        ${sheet.group_shot_helper ? `<div class="info-item"><div class="info-label">Group Shot Helper</div><div class="info-value">${esc(sheet.group_shot_helper)}</div></div>` : ''}
      `}
    </div>
    ${sheet.sheet_type === 'guestbook' ? `
        ${sheet.venue ? `<div class="info-item"><div class="info-label">Venue</div><div class="info-value">${esc(sheet.venue)}</div></div>` : ''}
        ${sheet.coordinator ? `<div class="info-item"><div class="info-label">Coordinator</div><div class="info-value">${esc(sheet.coordinator)}</div></div>` : ''}
        ${sheet.guest_count ? `<div class="info-item"><div class="info-label">Guests</div><div class="info-value">${esc(String(sheet.guest_count))}</div></div>` : ''}
        ${sheet.booth_start_time || sheet.booth_end_time ? `<div class="info-item"><div class="info-label">Booth Times</div><div class="info-value">${esc(sheet.booth_start_time || '?')} – ${esc(sheet.booth_end_time || '?')}</div></div>` : (sheet.booth_times_raw ? `<div class="info-item"><div class="info-label">Booth Times</div><div class="info-value">${esc(sheet.booth_times_raw)}</div></div>` : '')}
      </div>
      ${section('Instagram', sheet.instagram)}
      ${section('Entertainment & Surprises', sheet.entertainment)}
      ${section('Special Instructions / Timeline', sheet.special_instructions)}
      ${section('Additional Notes', sheet.anything_else)}
    ` : `
      ${sheet.has_video_booth ? '<div class="booth-badge">VIDEO BOOTH</div>' : ''}
      <div class="booth-badge" style="background:${sheet.supplier_meal ? '#dcfce7;color:#166534' : '#fef3c7;color:#92400e'}">${sheet.supplier_meal ? 'MEAL PROVIDED' : 'NO MEAL PROVIDED'}</div>
      ${sheet.timeline ? `<div class="timeline-header">Timeline</div><table class="timeline-table">${timelineRows}</table>` : ''}
      ${section('Group Shots', sheet.group_shots)}
      ${section('Readings & Speeches', sheet.readings_speeches)}
      ${section('Instagram', sheet.instagram)}
      ${section('Entertainment & Surprises', sheet.entertainment)}
      ${section('Potential Conflicts', sheet.punch_ups)}
      ${section('Children', sheet.children_details)}
      ${section('Other Suppliers', sheet.other_suppliers)}
      ${section('Additional Notes', sheet.anything_else)}
    `}
    <div class="footer">&copy; ${new Date().getFullYear()} Wedding Video Essex &middot; Confidential &amp; Protected Under NDA</div>
  </div>
</div></body></html>`;
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        URL.revokeObjectURL(blobUrl);
        printWindow.focus();
        setTimeout(() => printWindow.print(), 300);
      };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#605e5e]" />
            Prep Sheet
            {sheet?.send_status === 'sent' && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-2" data-testid="send-status-sent">Sent</span>
            )}
            {sheet?.send_status === 'failed' && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full ml-2" data-testid="send-status-failed">Failed to send</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {sheet ? `${sheet.person1_name || ''} & ${sheet.person2_name || ''} \u2014 ${formatDate(sheet.job_date)}` : 'Loading...'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#605e5e]" />
          </div>
        ) : sheet ? (
          <div className="space-y-4 py-2">
            {sheet.send_status === 'sent' ? (
              <div className="flex items-center gap-2.5 bg-green-600 text-white rounded-lg px-4 py-2.5" data-testid="sheet-sent-banner">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold">Complete &amp; Sent</p>
                  {sheet.sent_at && (
                    <p className="text-xs text-green-100">
                      {new Date(sheet.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {sheet.sent_to?.length > 0 && ` — sent to ${sheet.sent_to.join(', ')}`}
                    </p>
                  )}
                </div>
              </div>
            ) : isSheetOverdue(sheet.job_date) ? (
              <div className="flex items-center gap-2.5 bg-red-600 text-white rounded-lg px-4 py-2.5" data-testid="sheet-overdue-banner">
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm font-semibold">Sheet Overdue — wedding is under 2 weeks away and this sheet hasn't been sent</p>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-2" data-testid="sheet-pending-banner">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <p className="text-sm font-medium">Sheet Pending — not yet marked complete &amp; sent</p>
              </div>
            )}
            {/* Couple Header */}
            <div className="bg-[#605e5e] rounded-lg p-4 text-white">
              <h2 className="text-xl font-bold">
                {editMode ? (
                  <div className="flex gap-2">
                    <Input value={sheet.person1_name || ''} onChange={e => handleFieldChange('person1_name', e.target.value)} className="bg-white/10 border-white/20 text-white h-8 text-lg" placeholder="Person 1" />
                    <span className="text-lg self-center">&</span>
                    <Input value={sheet.person2_name || ''} onChange={e => handleFieldChange('person2_name', e.target.value)} className="bg-white/10 border-white/20 text-white h-8 text-lg" placeholder="Person 2" />
                  </div>
                ) : (
                  `${sheet.person1_name} & ${sheet.person2_name}`
                )}
              </h2>
              <p className="text-white/70 text-sm mt-1">
                {formatDate(sheet.job_date)}
                {sheet.entertainment_required && (
                  <span className="text-white/90 ml-1">— {sheet.entertainment_required}</span>
                )}
              </p>
              {sheet.team?.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {sheet.team.map((name) => (
                    <span key={name} className="text-xs bg-white/20 px-2 py-0.5 rounded">{name}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Couple Photo */}
            <div data-testid="couple-photo-section">
              {photoUrl ? (
                <div className="relative">
                  <img
                    src={photoUrl}
                    alt="Couple"
                    className="w-full max-h-64 object-cover rounded-lg"
                    data-testid="couple-photo-img"
                  />
                  {editMode && (
                    <button
                      onClick={handlePhotoDelete}
                      className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors"
                      data-testid="couple-photo-delete"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ) : editMode ? (
                <label
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-[#605e5e] hover:bg-gray-50 transition-colors"
                  data-testid="couple-photo-upload"
                >
                  {uploading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-[#605e5e]" />
                  ) : (
                    <>
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                      <span className="text-sm text-gray-500">Upload Couple Photo</span>
                      <span className="text-xs text-gray-400">Click to browse</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              ) : null}
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="h-4 w-4 text-[#605e5e] flex-shrink-0" />
                  <p className="text-xs text-[#8d8b8b]">{sheet.person1_name || 'Person 1'}</p>
                </div>
                {editMode ? (
                  <Input value={sheet.person1_phone || ''} onChange={e => handleFieldChange('person1_phone', e.target.value)} className="text-sm" data-testid="sheet-person1-phone" />
                ) : (
                  <PhoneActions phone={sheet.person1_phone} testid="sheet-person1-phone" />
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="h-4 w-4 text-[#605e5e] flex-shrink-0" />
                  <p className="text-xs text-[#8d8b8b]">{sheet.person2_name || 'Person 2'}</p>
                </div>
                {editMode ? (
                  <Input value={sheet.person2_phone || ''} onChange={e => handleFieldChange('person2_phone', e.target.value)} className="text-sm" data-testid="sheet-person2-phone" />
                ) : (
                  <PhoneActions phone={sheet.person2_phone} testid="sheet-person2-phone" />
                )}
              </div>
            </div>

            {sheet.sheet_type === 'guestbook' ? (
              /* ========== GUESTBOOK LAYOUT ========== */
              <>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[#e5e7eb] pt-3">
                  <EditableField label="Venue" field="venue" value={sheet?.venue || ''} editMode={editMode} onChange={handleFieldChange} mapLink />
                  <EditableField label="Coordinator" field="coordinator" value={sheet?.coordinator || ''} editMode={editMode} onChange={handleFieldChange} />
                  <EditableField label="Guest Count" field="guest_count" value={sheet?.guest_count || ''} editMode={editMode} onChange={handleFieldChange} />
                  <EditableField label="Instagram" field="instagram" value={sheet?.instagram || ''} editMode={editMode} onChange={handleFieldChange} />
                </div>
                <div className="border-t border-[#e5e7eb] pt-3">
                  <p className="text-xs text-[#8d8b8b] mb-1">Booth Operation Times</p>
                  <div className="flex items-center gap-2">
                    {editMode ? (
                      <>
                        <Input value={sheet.booth_start_time || ''} onChange={e => handleFieldChange('booth_start_time', e.target.value)} placeholder="Start (e.g. 3pm)" className="text-sm flex-1" data-testid="booth-start-time" />
                        <span className="text-[#8d8b8b] text-sm">to</span>
                        <Input value={sheet.booth_end_time || ''} onChange={e => handleFieldChange('booth_end_time', e.target.value)} placeholder="End (e.g. 10pm)" className="text-sm flex-1" data-testid="booth-end-time" />
                      </>
                    ) : (
                      <p className="text-sm font-medium">{sheet.booth_start_time && sheet.booth_end_time ? `${sheet.booth_start_time} – ${sheet.booth_end_time}` : (sheet.booth_times_raw || '\u2014')}</p>
                    )}
                  </div>
                  {sheet.booth_times_raw && editMode && (
                    <p className="text-xs text-[#8d8b8b] mt-1 italic">From form: {sheet.booth_times_raw}</p>
                  )}
                </div>
                <div className="space-y-3 border-t border-[#e5e7eb] pt-3">
                  {(sheet.entertainment || editMode) && <EditableField label="Entertainment & Surprises" field="entertainment" multiline value={sheet?.entertainment || ''} editMode={editMode} onChange={handleFieldChange} />}
                  <EditableField label="Special Instructions / Timeline" field="special_instructions" multiline value={sheet?.special_instructions || ''} editMode={editMode} onChange={handleFieldChange} />
                  {(sheet.anything_else || editMode) && <EditableField label="Additional Notes" field="anything_else" multiline value={sheet?.anything_else || ''} editMode={editMode} onChange={handleFieldChange} />}
                </div>
              </>
            ) : (
              /* ========== WEDDING LAYOUT ========== */
              <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[#e5e7eb] pt-3">
              {(!sheet.ceremony_venue && !sheet.reception_venue) && (
                <EditableField label="Venue" field="venue" value={sheet?.venue || ''} editMode={editMode} onChange={handleFieldChange} mapLink />
              )}
              {sheet.ceremony_venue && (
                <EditableField label="Ceremony Venue" field="ceremony_venue" value={sheet?.ceremony_venue || ''} editMode={editMode} onChange={handleFieldChange} mapLink />
              )}
              {sheet.reception_venue && (
                <EditableField label="Reception Venue" field="reception_venue" value={sheet?.reception_venue || ''} editMode={editMode} onChange={handleFieldChange} mapLink />
              )}
              <EditableField label="Coordinator" field="coordinator" value={sheet?.coordinator || ''} editMode={editMode} onChange={handleFieldChange} />
              <EditableField label="Guests" field="guest_count" value={sheet?.guest_count || ''} editMode={editMode} onChange={handleFieldChange} />
              <EditableField label="Group Shot Helper" field="group_shot_helper" value={sheet?.group_shot_helper || ''} editMode={editMode} onChange={handleFieldChange} />
            </div>

            {/* Video Booth + Supplier Meal (inline) */}
            <div className="flex items-center gap-2 flex-wrap">
              {sheet.has_video_booth && (
                <div className="inline-block bg-[#605e5e] text-white text-sm font-bold px-4 py-1.5 rounded">
                  VIDEO BOOTH
                </div>
              )}

              {editMode ? (
                <button
                  onClick={() => handleFieldChange('supplier_meal', !sheet.supplier_meal)}
                  className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-1.5 rounded cursor-pointer transition-colors ${sheet.supplier_meal ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
                  data-testid="sheet-meal-toggle"
                >
                  {sheet.supplier_meal ? 'Meal Provided' : 'No Meal Provided'}
                  <span className="text-[10px] opacity-60">(click to toggle)</span>
                </button>
              ) : (
                <div className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-1.5 rounded ${sheet.supplier_meal ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-testid="sheet-meal">
                  {sheet.supplier_meal ? 'Meal Provided' : 'No Meal Provided'}
                </div>
              )}
            </div>

            {/* Text Sections */}
            <div className="space-y-3 border-t border-[#e5e7eb] pt-3">
              <EditableField label="Timeline" field="timeline" multiline value={sheet?.timeline || ''} editMode={editMode} onChange={handleFieldChange} linkifyAddresses />
              <EditableField label="Group Shots" field="group_shots" multiline value={sheet?.group_shots || ''} editMode={editMode} onChange={handleFieldChange} />
              <EditableField label="Readings & Speeches" field="readings_speeches" multiline value={sheet?.readings_speeches || ''} editMode={editMode} onChange={handleFieldChange} />
              <EditableField label="Instagram" field="instagram" value={sheet?.instagram || ''} editMode={editMode} onChange={handleFieldChange} />
              <EditableField label="Entertainment & Surprises" field="entertainment" multiline value={sheet?.entertainment || ''} editMode={editMode} onChange={handleFieldChange} />
              {(sheet.punch_ups || editMode) && <EditableField label="Potential Conflicts" field="punch_ups" multiline value={sheet?.punch_ups || ''} editMode={editMode} onChange={handleFieldChange} />}
              {(sheet.children_details || editMode) && <EditableField label="Children" field="children_details" value={sheet?.children_details || ''} editMode={editMode} onChange={handleFieldChange} />}
              {(sheet.other_suppliers || editMode) && <EditableField label="Other Suppliers" field="other_suppliers" multiline value={sheet?.other_suppliers || ''} editMode={editMode} onChange={handleFieldChange} />}
              {(sheet.anything_else || editMode) && <EditableField label="Additional Notes" field="anything_else" multiline value={sheet?.anything_else || ''} editMode={editMode} onChange={handleFieldChange} />}
            </div>
            </>
            )}

            {sheet.send_status === 'sent' && sheet.sent_at && (
              <div className="text-xs text-green-600 text-center bg-green-50 rounded-md p-2" data-testid="send-confirmation">
                <Check className="h-3 w-3 inline mr-1" />
                Sent on {new Date(sheet.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {sheet.sent_to?.length > 0 && ` to ${sheet.sent_to.join(', ')}`}
              </div>
            )}
            {sheet.send_status === 'failed' && (
              <div className="text-xs text-red-700 bg-red-50 rounded-md p-2 space-y-1" data-testid="send-failure">
                <div className="font-semibold flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Email failed to send
                </div>
                {sheet.send_error && <p className="text-red-500">{sheet.send_error}</p>}
                {editMode && (
                  <Button variant="outline" size="sm" onClick={handleSend} disabled={sending} className="mt-1 h-6 text-xs border-red-300 text-red-700 hover:bg-red-100">
                    {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                    Retry Send
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-[#8d8b8b] py-8">No data available</p>
        )}

        {sheet && (
          <DialogFooter className="flex gap-2 sm:gap-2 border-t border-[#e5e7eb] pt-3">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="sheet-print-btn">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            {editMode && (
              <>
                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading} data-testid="sheet-refresh-btn">
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh from Typeform
                </Button>
                {Object.keys(editing).length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleSave} data-testid="sheet-save-btn">
                    <Check className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={sending}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="sheet-send-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {sheet.send_status === 'sent' ? 'Resend' : sheet.send_status === 'failed' ? 'Retry Send' : 'Mark as Complete & Send'}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
